import React, { useState, useEffect } from "react";
import MerchantLayout from "src/components/Layouts/MerchantLayout";
import { NormalText } from "src/components/styled/text.styled";
import { Button } from "src/components/styled/button.styled";
import Tables from "src/components/Table/tableData";
import {
  Box,
  Stack,
  Grid,
  Button as ButtonMUI,
  Typography,
  Autocomplete,
  TextField,
  Tooltip,
} from "@mui/material";
import InputDate from "src/components/Inputs/DatePicker";
import Dropdown from "src/components/Inputs/Dropdown";
import OrderGroups from "../layout";
import useRedux from "src/redux/useRedux";
import { getList, OrderProps, downloadReport } from "src/redux/orderReport";
import moment from "moment";
import FilterStore from "src/views/Order/storeFilter";
import axios, { AxiosRequestConfig, CancelTokenSource } from "axios";
import { formatCurrency, getToken } from "src/helper/generalFunction";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { getAllStore, ListStoreData } from "src/redux/storelist";

interface LocationState {
  store_name?: string;
}

const OrderReportPage = () => {
  const formatDateConsistent = (date: string | Date | null) => {
    if (!date) return '';
    return moment(date).format('DD-MM-YYYY');
  };
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const [download, setDownload] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<OrderProps[]>();
  const [totalData, setTotalData] = useState(0);
  
  const [filterStore, setFilterStore] = useState<{ id: number; store_name: string }>({ 
    id: 0, 
    store_name: "Semua Toko" 
  });
  
  const [filterCashier, setFilterCashier] = useState("");
  const [stores, setStores] = useState<ListStoreData[]>([]);
  const [availableCashiers, setAvailableCashiers] = useState<string[]>([]);
  const [filterPayload, setFilterPayload] = React.useState<{
    start_date: string | null;
    end_date: string | null;
    status: string;
    product_type: string;
  }>({
    start_date: null,
    end_date: null,
    status: "",
    product_type: "",
  });
  const [downloadProgress, setDownloadProgress] = useState(0);

  const columns = [
    {
      name: "Tanggal",
      wrap: true,
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {moment(row.payment_date).format('DD-MM-YYYY HH:mm:ss')}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Order No",
      wrap: true,
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {row.order_number}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Nama Kasir",
      wrap: true,
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {row.cashier_name}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Nama Customer",
      wrap: true,
      width: "12%",
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {row.customer_name}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Nama Produk",
      wrap: true,
      selector: (row: any) => {
        return row.order_detail && row.order_detail.length !== 0
          ? row.order_detail.map((item: any, i: number) => (
              <NormalText textAlign="center" fontSize={"12px"} key={i}>
                - {item.product_name}
              </NormalText>
            ))
          : null;
      },
      sortable: true,
    },
    {
      name: "Tipe Pembayaran",
      width: "12%",
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {row.payment_type.replace("_", " ")}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Nominal",
      selector: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {formatCurrency(Number(row.total_amount_payment))}
          </NormalText>
        );
      },
      sortable: true,
    },
    {
      name: "Status",
      cell: (row: any) => {
        return (
          <NormalText textAlign="center" fontSize={"12px"}>
            {row.status_order.replace("_", " ")}
          </NormalText>
        );
      },
      sortable: false,
    },
    {
      name: "Aksi",
      center: true,
      width: "10%",
      style: { textAlign: "left", paddingLeft: "5px", paddingRight: "5px" },
      cell: (row: any) => {
        return (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <ButtonMUI
              variant="contained"
              color="primary"
              onClick={() => navigate(`/orders/${row.order_number}/detail`)}
              sx={{ width: 80, height: 20, fontSize: 10 }}
            >
              Detail
            </ButtonMUI>
          </Grid>
        );
      },
    },
  ];

  const {
    thunkDispatch,
    storeState: {
      Auth: {
        status,
        data: { user_type, profile_data },
      },
      AppOption: { selected_store },
    },
  } = useRedux();

  useEffect(() => {
    thunkDispatch(getAllStore({ limit: 0, offset: 0 }))
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          console.log("Stores loaded successfully:", res.data.data);
          setStores(res.data.data);
          
          if (locationState && locationState.store_name) {
            const receivedStoreName = locationState.store_name;
            const matchingStore = res.data.data.find((store: ListStoreData) =>
              store.store_name?.trim().toLowerCase() === receivedStoreName?.trim().toLowerCase()
            );

            if (matchingStore) {
              setFilterStore({
                id: matchingStore.id,
                store_name: matchingStore.store_name
              });
              toast.success(`Auto-selected store: ${matchingStore.store_name}`);
            } else {
              toast.warning("Store not found, please select manually");
            }
          }
        }
      })
      .catch((err) => {
        console.error("Error loading stores:", err);
        toast.error("Error loading stores");
      });
  }, [thunkDispatch]);

  useEffect(() => {
    if (locationState && locationState.store_name && stores.length > 0) {
      const receivedStoreName = locationState.store_name;
      const matchingStore = stores.find((store: ListStoreData) =>
        store.store_name?.trim().toLowerCase() === receivedStoreName?.trim().toLowerCase()
      );

      if (matchingStore && (!filterStore || filterStore.id !== matchingStore.id)) {
        setFilterStore({
          id: matchingStore.id,
          store_name: matchingStore.store_name
        });
      }
    }
  }, [locationState, stores]);

  const loadCashierData = async (storeId: number, cancelToken: CancelTokenSource) => {
    if (!storeId) {
      setAvailableCashiers([]);
      return;
    }

    try {
      const config = {
        headers: {
          api_key: `${process.env.REACT_APP_API_KEY}`,
          token: getToken(),
        },
        cancelToken: cancelToken.token,
      };

      const url = `/merchant/order/reports/data?limit=100&offset=0&store_id=${storeId}`;
      const response = await axios.get<{ data: OrderProps[] }>(`${process.env.REACT_APP_API_URL}${url}`, config);

      if (response.data && response.data.data) {
        const cashiers: string[] = Array.from(new Set(
          response.data.data
            .filter((item) => item.cashier_name)
            .map((item) => item.cashier_name)
        ));
        setAvailableCashiers(cashiers);
      } else {
        setAvailableCashiers([]);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Cashier data fetch cancelled");
        return;
      }
      console.error("Error loading cashier data:", error);
      toast.error("Error loading cashier data");
      setAvailableCashiers([]);
    }
  };

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    if (filterStore?.id && filterStore.id !== 0) {
      setAvailableCashiers([]);
      loadCashierData(filterStore.id, cancelToken);
    } else {
      setAvailableCashiers([]);
      setFilterCashier("");
    }

    return () => {
      cancelToken.cancel("Cancelled due to new store selection");
    };
  }, [filterStore?.id]);

  const getAll = () => {
    setLoading(true);
    let storeId = filterStore?.id ? String(filterStore.id) : "";
    if (
      status &&
      status === "success" &&
      user_type &&
      user_type !== "merchant"
    ) {
      storeId = String(
        selected_store && selected_store.id ? selected_store.id : ""
      );
    }

    let dataObject = {
      offset: offset - 1,
      limit: limit,
      status_order: filterPayload.status === "ALL_STATUS" ? "" : filterPayload.status,
      start_date: filterPayload.start_date
        ? moment(filterPayload.start_date, 'DD-MM-YYYY').startOf('day').format("YYYY-MM-DD HH:mm:ss")
        : null,
      end_date: filterPayload.end_date
        ? moment(filterPayload.end_date, 'DD-MM-YYYY').endOf('day').format("YYYY-MM-DD HH:mm:ss")
        : null,
      store_id: storeId === "0" ? "" : storeId,
      product_type: filterPayload.product_type === "ALL_TYPES" ? "" : filterPayload.product_type,
      cashier_name: filterCashier === "ALL_CASHIERS" ? "" : filterCashier || undefined,
    };

    Object.keys(dataObject).forEach(key => {
      if (dataObject[key as keyof typeof dataObject] === undefined || 
          dataObject[key as keyof typeof dataObject] === null || 
          dataObject[key as keyof typeof dataObject] === "") {
        delete dataObject[key as keyof typeof dataObject];
      }
    });

    console.log("Fetching orders with parameters:", dataObject);

    thunkDispatch(getList(dataObject))
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          console.log("Order data response:", res.data.data);
          if (res.data.data.length === 0) {
            setData([]);
            setTotalData(0);
            toast.warning(
              filterCashier 
                ? `Tidak ada data untuk kasir "${filterCashier}" pada tanggal yang dipilih.`
                : "Tidak ada data untuk tanggal yang dipilih."
            );
          } else {
            const responseCashiers = Array.from(new Set(
              res.data.data
                .filter((item: OrderProps) => item.cashier_name)
                .map((item: OrderProps) => item.cashier_name)
            ));
            console.log("Cashiers in table data:", responseCashiers);

            if (filterCashier && filterCashier !== "ALL_CASHIERS") {
              const filteredByCashier = res.data.data.filter((item: OrderProps) => 
                item.cashier_name === filterCashier
              );
              console.log(`Data filtered by cashier "${filterCashier}":`, filteredByCashier.length, "items");
              
              if (filteredByCashier.length === 0) {
                toast.warning(`Tidak ada data untuk kasir "${filterCashier}"`);
                setData([]);
                setTotalData(0);
              } else {
                setData(filteredByCashier);
                setTotalData(filteredByCashier.length);
                toast.success(`Menampilkan ${filteredByCashier.length} data untuk kasir "${filterCashier}"`);
              }
            } else {
              setData(res.data.data);
              setTotalData(
                res.data.meta && res.data.meta.total
                  ? res.data.meta.total
                  : res.data.data.length
              );
            }
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error fetching order data:", err);
        toast.error("Error fetching order data");
      });
  };

  const handleStoreFilterChange = (selectedStore: any) => {
    console.log("Store filter changed to:", selectedStore);
    const storeToSet = selectedStore || { id: 0, store_name: "Semua Toko" };
    
    setFilterStore(storeToSet);
    setFilterCashier("");
  };

  const handleExport = async () => {
    setDownload(true);
    const headers = {
      "Content-Type": "blob",
      api_key: `${process.env.REACT_APP_API_KEY}`,
      token: getToken(),
    };
    let startDate = filterPayload.start_date
      ? moment(filterPayload.start_date, 'DD-MM-YYYY').format("YYYY-MM-DD")
      : null;
    let endDate = filterPayload.end_date
      ? moment(filterPayload.end_date, 'DD-MM-YYYY').format("YYYY-MM-DD")
      : null;
    let storeId = filterStore?.id ? String(filterStore.id) : "";
    if (user_type && user_type !== "merchant") {
      storeId = String(
        selected_store && selected_store.id ? selected_store.id : ""
      );
    }
    let url = `/merchant/order/reports/data/download?${
      startDate ? `start_date=${startDate}` : ""
    }${endDate ? `&end_date=${endDate}` : ""}${
      filterPayload.status && filterPayload.status !== "ALL_STATUS" ? `&status_order=${filterPayload.status}` : ""
    }${storeId && storeId !== "0" ? `&store_id=${storeId}` : ""}${
      filterCashier && filterCashier !== "ALL_CASHIERS" ? `&cashier_name=${encodeURIComponent(filterCashier)}` : ""
    }${filterPayload.product_type && filterPayload.product_type !== "ALL_TYPES" ? `&product_type=${filterPayload.product_type}` : ""}`;
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${process.env.REACT_APP_API_URL}${url}`,
      responseType: "arraybuffer",
      headers,
      onDownloadProgress: (progressEvent) => {
        const percentage = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        setDownloadProgress(percentage);
      },
    };

    try {
      const response = await axios(config);

      const outputFilename =
        user_type !== "merchant"
          ? `Laporan Order - ${selected_store.name}${
              filterCashier && filterCashier !== "ALL_CASHIERS" ? ` - ${filterCashier}` : ""
            }${
              filterPayload.start_date
                ? " - " +
                  moment(filterPayload.start_date, 'DD-MM-YYYY').format("DD MMMM YYYY")
                : ""
            }${
              filterPayload?.end_date
                ? " - " + moment(filterPayload.end_date, 'DD-MM-YYYY').format("DD MMMM YYYY")
                : ""
            }.xlsx`
          : `Laporan Order - ${profile_data.name}${
              filterStore && filterStore.store_name
                ? " - " + filterStore.store_name
                : ""
            }${
              filterCashier && filterCashier !== "ALL_CASHIERS" ? " - " + filterCashier : ""
            }${
              filterPayload.start_date
                ? " - " +
                  moment(filterPayload.start_date, 'DD-MM-YYYY').format("DD MMMM YYYY")
                : ""
            }${
              filterPayload?.end_date
                ? " - " + moment(filterPayload.end_date, 'DD-MM-YYYY').format("DD MMMM YYYY")
                : ""
            }${
              !startDate && !endDate
                ? " - " + moment().format("DD MMMM YYYY")
                : ""
            }.xlsx`;
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error downloading report");
      setDownload(false);
    }
  };

  React.useEffect(() => {
    if (offset !== 1) {
      setOffset(1);
    } else {
      getAll();
    }
  }, [selected_store, filterStore, filterCashier, limit, filterPayload]);

  React.useEffect(() => {
    if (filterStore?.id !== undefined) {
      getAll();
    }
  }, [offset]);

  React.useEffect(() => {
    if (downloadProgress === 100) {
      setTimeout(() => {
        setDownload(false);
        setDownloadProgress(0);
      }, 500);
    }
  }, [downloadProgress]);

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <MerchantLayout>
      <OrderGroups tabActive={0}>
        <Box
          sx={{ background: "white", borderRadius: "10px" }}
          padding={"20px"}
        >
          <NormalText fontSize="20px" fontWeight="bold" margin={"0 0 20px 0"}>
            Filter
          </NormalText>
          <Grid container>
            <Grid item md={2} xs={12} sm={12}>
              <NormalText fontSize="15px" fontWeight="bold">Pilih Tanggal</NormalText>
            </Grid>
            <Grid item md={6}>
              <Stack direction={'row'} spacing={2} alignItems={'center'}>
                <InputDate
                  value={filterPayload.start_date}
                  disableFuture
                  onChange={(v: any) => {
                    setFilterPayload((prev) => ({
                      ...prev,
                      start_date: v,
                      end_date: moment(v, "DD-MM-YYYY").isAfter(moment(prev.end_date, "DD-MM-YYYY"))
                        ? v
                        : prev.end_date,
                    }));
                  }}
                />
                <InputDate
                  disableFuture
                  value={filterPayload.end_date}
                  minDate={moment(filterPayload.start_date, "DD-MM-YYYY").toDate()}
                  onChange={(v: any) => {
                    if (moment(v, "DD-MM-YYYY").isBefore(moment(filterPayload.start_date, "DD-MM-YYYY"))) {
                      toast.warning('Tanggal akhir tidak boleh sebelum tanggal awal');
                    } else {
                      setFilterPayload({ ...filterPayload, end_date: v });
                    }
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}>
              <NormalText fontSize="15px" fontWeight="bold">
                Pilih Status
              </NormalText>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box width={"250px"}>
                <Dropdown
                  onChange={(e: any) =>
                    setFilterPayload({ ...filterPayload, status: e })
                  }
                  value={filterPayload.status}
                  options={[
                    { label: "Semua Status", value: "ALL_STATUS" },
                    { label: "Order Baru", value: "NEW_ORDER" },
                    { label: "Proses", value: "PROCESS" },
                    { label: "Pesanan Siap", value: "READY_TO_PICKUP" },
                    { label: "Dikirim", value: "DELIVERY" },
                    { label: "Selesai", value: "CLOSED" },
                    { label: "Batal", value: "CANCELED" },
                  ]}
                ></Dropdown>
              </Box>
            </Grid>
          </Grid>
          {user_type && user_type === "merchant" && (
            <FilterStore
              value={filterStore}
              onChange={handleStoreFilterChange}
              filterType={"report"}
              stores={stores}
            />
          )}
          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}>
              <NormalText fontSize="15px" fontWeight="bold">
                Pilih Nama Kasir
              </NormalText>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Autocomplete
                id="combo-box-cashier-list"
                options={["ALL_CASHIERS", ...availableCashiers]}
                getOptionLabel={(option) => option === "ALL_CASHIERS" ? "Semua Kasir" : option}
                value={filterCashier || null}
                onChange={(event: any, newValue: any) => {
                  setFilterCashier(newValue || "");
                  console.log("Cashier selected:", newValue);
                }}
                disabled={!filterStore?.id || filterStore.id === 0}
                sx={{ width: "250px" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    style={{ fontSize: 12 }}
                    size="small"
                    placeholder={filterStore?.id && filterStore.id !== 0 ? "Pilih Kasir" : "Pilih toko terlebih dahulu"}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Tooltip title={option === "ALL_CASHIERS" ? "Semua Kasir" : option} placement="right">
                      <span>{truncateText(option === "ALL_CASHIERS" ? "Semua Kasir" : option, 30)}</span>
                    </Tooltip>
                  </Box>
                )}
              />
            </Grid>
          </Grid>
          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}>
              <NormalText fontSize="15px" fontWeight="bold">
                Pilih Tipe Produk
              </NormalText>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box width={"250px"}>
                <Dropdown
                  onChange={(e: any) =>
                    setFilterPayload({ ...filterPayload, product_type: e })
                  }
                  value={filterPayload.product_type}
                  options={[
                    { label: "Semua Tipe", value: "ALL_TYPES" },
                    { label: "Produk Global", value: "global" },
                    { label: "Produk Toko", value: "store" },
                  ]}
                ></Dropdown>
              </Box>
            </Grid>
          </Grid>
          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}></Grid>
            <Grid item xs={12} sm={12} md={10}>
              <Stack direction={"row"}>
                <Button variant="primary" onClick={() => getAll()}>
                  Filter
                </Button>
                <Button
                  variant="primary"
                  background="#21a421 !important"
                  margin="0 0 0 10px"
                  onClick={() => {
                    handleExport().then((res) => {
                      console.log("ok", res);
                    });
                  }}
                  disabled={download === true}
                >
                  Download Excel
                </Button>
                {download ? (
                  <div className="ml-3">
                    <Typography fontSize={"0.8rem"} className="mb-1">
                      Mempersiapkan laporan anda ...
                    </Typography>
                    <div
                      className="progress"
                      role="progressbar"
                      aria-label="Animated striped example"
                    >
                      <div
                        className="progress-bar progress-bar-striped bg-success progress-bar-animated"
                        style={{
                          width: `${downloadProgress ? downloadProgress : 0}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
          <Tables
            hasContainer={false}
            records={data}
            columns={columns}
            valueSearch={search}
            progressPending={loading}
            paginationPerPage={limit}
            totalRow={totalData}
            onChangePage={(page) => setOffset(page)}
            onChangeRowsPerPage={(currentRowsPerPage) =>
              setLimit(currentRowsPerPage)
            }
          />
        </Box>
      </OrderGroups>
    </MerchantLayout>
  );
};

export default OrderReportPage;
